import ResultActions from './ResultActions';
import FitAdvisor from './FitAdvisor';
import NeighborSizes from './NeighborSizes';
import BetweenSizesAlert from './BetweenSizesAlert';
import MatchConfidence from './MatchConfidence';
import { getBetweenSizesWarning, getMatchConfidence } from '../../utils/sizeInsights';

export default function ResultCards({
  result,
  categoryId,
  t,
  inputSystem,
  neighborSizes,
  fitPreference,
  onFitChange,
  onPickSize,
  summaryText,
  shareUrl,
}) {
  if (!result) return null;

  const { row, inputValue } = result;
  const genderLabel = row.gender === 'women' ? 'women' : row.gender === 'kids' ? 'kids' : 'men';

  const fieldsByCategory = {
    shoes: ['eu', 'uk', 'us', 'cm'],
    'kids-shoes': ['eu', 'uk', 'us', 'cm', 'age'],
    jeans: ['waistIn', 'eu', 'uk', 'us', 'international', 'waistCm', 'hipsCm', 'inseamCm'],
    'mens-clothing': ['international', 'eu', 'uk', 'us', 'chestCm', 'waistCm', 'hipsCm'],
    'womens-clothing': ['international', 'eu', 'uk', 'us', 'chestCm', 'waistCm', 'hipsCm'],
  };

  const fields = fieldsByCategory[categoryId] || fieldsByCategory['mens-clothing'];
  const inputLabel = t.systems[inputSystem] || t.results[inputSystem] || inputSystem;
  const betweenWarning = getBetweenSizesWarning(result, neighborSizes, t);
  const confidence = getMatchConfidence(result);

  return (
    <div className="mt-6 animate-fade-in">
      <div className="rounded-lg bg-slate-900/80 border border-emerald-600/30 px-4 py-2 mb-4 text-sm text-slate-300 flex flex-wrap items-center gap-2 justify-between">
        <span>
          {t.converter.yourInput}:{' '}
          <span className="text-emerald-400 font-semibold">
            {inputLabel} {inputValue}
          </span>
          {result.matchedByMeasurement && (
            <span className="text-slate-500 ml-2">({t.measureFinder.title})</span>
          )}
        </span>
        <MatchConfidence confidence={confidence} t={t} />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <h3 className="text-lg font-semibold text-white">{t.converter.resultsTitle}</h3>
        <span className="text-xs rounded-full bg-amber-500/20 text-amber-300 px-2 py-1 border border-amber-500/30">
          {t.converter.approximate}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {fields
          .filter((key) => row[key] && key !== inputSystem)
          .map((key, i) => (
            <div
              key={key}
              className="rounded-xl border border-slate-700 bg-slate-800/80 p-4 transition-transform hover:scale-[1.02] hover:border-emerald-600/40 animate-slide-up"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                {t.results[key] || key}
                {categoryId === 'shoes' && (key === 'uk' || key === 'us') && (
                  <span className="normal-case text-slate-500"> ({genderLabel})</span>
                )}
              </p>
              <p className="text-2xl font-bold text-emerald-400">{row[key]}</p>
              {key === 'cm' && (
                <p className="text-xs text-slate-500 mt-1">approx. {row[key]} cm</p>
              )}
            </div>
          ))}
      </div>

      <BetweenSizesAlert warning={betweenWarning} />

      <NeighborSizes
        t={t}
        neighbors={neighborSizes}
        systemId={inputSystem}
        systemLabel={inputLabel}
        onPick={onPickSize}
      />

      <FitAdvisor
        t={t}
        fitPreference={fitPreference}
        onFitChange={onFitChange}
        neighborSizes={neighborSizes}
        onPickSize={onPickSize}
        systemId={inputSystem}
      />

      <ResultActions t={t} summaryText={summaryText} shareUrl={shareUrl} />

      <p className="mt-4 text-sm text-slate-400 border-l-2 border-emerald-600 pl-3">
        {t.converter.recommendation}
      </p>
    </div>
  );
}
